/* ============================================
   Portal Cliente — Vista Pública de Cotización
   Accesible por public_token sin autenticación
   OCULTA: márgenes, costos internos
   ============================================ */
import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { showToast } from '@/components/layout/Toast'
import { descargarPdf } from '@/lib/documentos'
import type { Cotizacion, Empresa, CotizacionItemsEquipo, CotizacionItemsTareas, CotizacionTarea } from '@/lib/types'
import { formatCurrency, formatDate, getDiscountTier, fmtPct, processContent, fmtFTE, r2 } from '@/lib/utils'
import TaskDetailPages from '@/components/cotizaciones/TaskDetailPages'
import { DatosBancarios } from '@/components/cotizaciones/DatosBancarios'
import PaginatedSection from '@/components/cotizaciones/PaginatedSection'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

export default function PortalCliente() {
  const { token } = useParams<{ token: string }>()
  const pdfRef = useRef<HTMLDivElement>(null)

  const [cot, setCot] = useState<Cotizacion | null>(null)
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        // Rutas públicas del backend: esta vista no tiene sesión, así que no
        // puede pasar por la capa de compat (`/api/cotizaciones` exige JWT y
        // devolvía 401 — el enlace que se manda al cliente no cargaba).
        const [cotRes, empRes] = await Promise.all([
          fetch(`${API_URL}/api/cotizaciones/public/${encodeURIComponent(token!)}`),
          fetch(`${API_URL}/api/empresa`),
        ])

        if (cotRes.status === 404) { setError('Cotización no encontrada o enlace inválido.'); return }
        if (!cotRes.ok || !empRes.ok) throw new Error('Respuesta no OK')

        const cotData = await cotRes.json()
        const empData = await empRes.json()
        setCot(cotData as Cotizacion)
        setEmpresa((Array.isArray(empData) ? empData[0] ?? null : empData) as Empresa | null)
      } catch { setError('No se pudo cargar la cotización.') }
      finally { setLoading(false) }
    })()
  }, [token])

  /*
    La descarga es el PDF DE VERDAD, el mismo que arma el backend con
    Gotenberg y que se manda por correo.

    Antes esto rasterizaba la pantalla con html2canvas y pegaba las imágenes
    en un jsPDF. El resultado llegó a un cliente con el logo de la marca y las
    tarjetas de cifras EN BLANCO: html2canvas no dibuja todo lo que el
    navegador sí muestra, y nadie lo ve hasta que el documento ya está
    afuera. Además salía como foto — texto que no se puede copiar ni buscar.

    El backend lo arma desde los mismos datos, con la marca del partner, los
    cortes de página del template y el texto vectorial.
  */
  const exportPDF = async () => {
    if (!cot || !token) return
    setExporting(true)
    try {
      await descargarPdf(
        `${API_URL}/api/pdf/public/cotizacion/${token}/archivo`,
        `${cot.proyecto_nombre || 'Cotizacion'} - ${cot.cliente_nombre || cot.numero}`,
      )
    } catch { showToast('No se pudo descargar el documento. Vuelve a intentarlo.', 'error') }
    finally { setExporting(false) }
  }

  // ─── Loading state ───
  if (loading) return (
    <div className="portal-wrapper">
      <div className="portal-loading">
        <div className="auth-spinner" style={{ width: 40, height: 40, margin: '0 auto 16px' }} />
        <p>Cargando cotización…</p>
      </div>
    </div>
  )

  // ─── Error state ───
  if (error || !cot || !empresa) return (
    <div className="portal-wrapper">
      <div className="portal-error">
        <h2>Enlace inválido</h2>
        <p>{error || 'La cotización solicitada no existe o ya no está disponible.'}</p>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 12 }}>Si crees que esto es un error, contacta a tu asesor comercial.</p>
      </div>
    </div>
  )

  /*
    Cotización cuyo documento es un ARCHIVO subido. Se sirve el PDF y nada
    más: sus `items` y `secciones` están vacíos, así que todo lo que sigue
    acá abajo armaría un documento con la tabla sin filas y el total en cero
    — con la marca del partner encima, que es lo que vería su cliente.

    No se le pone encabezado ni pie de nuestra parte: el documento ya trae la
    identidad de quien lo hizo, y agregarle otra lo contradiría.
  */
  if (cot.documento_subido) {
    const archivo = `${API_URL}/api/pdf/public/cotizacion/${encodeURIComponent(token!)}/archivo`
    return (
      <div className="portal-wrapper portal-wrapper--archivo">
        <div className="portal-archivo__barra">
          <span className="portal-archivo__titulo">{cot.proyecto_nombre || `Cotización ${cot.numero}`}</span>
          {/* `download` y no un segundo visor: quien abre esto desde el celular
              ve el PDF del sistema, y lo que le falta es guardarlo. */}
          <a className="btn btn-sm btn-secondary" href={archivo} download={`cotizacion-${cot.numero}.pdf`}>
            Descargar PDF
          </a>
        </div>
        <iframe
          className="portal-archivo__marco"
          src={archivo}
          title={`Cotización ${cot.numero}`}
        />
      </div>
    )
  }

  const isEquipo = cot.tipo_cotizacion === 'equipo_dedicado'
  const secciones = Array.isArray(cot.secciones) ? cot.secciones : []
  const pc = empresa.color_primario || '#1C4587'
  const proyectoNombre = cot.proyecto_nombre || 'Propuesta de Servicios'

  // White-label por cotización: esta es la vista PÚBLICA (/ver/:token), la que se le
  // manda al cliente final. Si la cotización trae branding de partner, el documento no
  // puede mostrar la identidad del emisor — ni razón social, ni RUC, ni los datos
  // bancarios, ni el pie de página. Mismo criterio que el template del PDF
  // (api/src/templates/cotizacion/index.hbs, `{{#unless cotizacion.branding}}`).
  // La condición es `branding.nombre`, no `branding`: ese objeto lleva también
  // el modo de precio (`presupuesto_mas_igv`), que no es marca blanca. Mismo
  // criterio en las otras dos superficies.
  const brand = cot.branding ?? null
  const isBranded = !!brand?.nombre
  const dispNombre = isBranded ? (brand?.nombre || '') : (empresa.nombre || '1Conecta')
  const dispWebsite = isBranded ? (brand?.website || '') : (empresa.website || '')




  // Equipo items (filtered — no costs/margins)
  const equipoData = isEquipo && cot.items && typeof cot.items === 'object' && 'equipo' in cot.items
    ? cot.items as CotizacionItemsEquipo : null
  const equipoMembers = equipoData?.equipo ?? []
  const equipoTotales = equipoData?.totales

  const tier = getDiscountTier(cot.descuento_porcentaje)

  // Tareas data
  const isTareas = cot.tipo_cotizacion === 'tareas'
  const tareasData = isTareas && cot.items && typeof cot.items === 'object' && 'tareas' in cot.items ? cot.items as CotizacionItemsTareas : null
  const tareasItems: CotizacionTarea[] = tareasData?.tareas ?? []

  return (
    <div className="portal-wrapper">
      {/* Top bar */}
      <div className="portal-topbar" style={{ background: pc }}>
        <div className="portal-topbar-inner">
          <div className="portal-brand">
            {brand?.logo_url
              ? <div style={{ background: '#fff', borderRadius: 8, padding: '4px 10px', display: 'inline-block' }}><img src={brand.logo_url} alt={dispNombre} style={{ height: 24, width: 'auto', display: 'block' }} /></div>
              : isBranded ? <strong style={{ color: '#fff' }}>{dispNombre}</strong>
              : empresa.logo_url ? <img src={empresa.logo_url} alt={empresa.nombre ?? ''} style={{ height: 32 }} /> : <strong style={{ color: '#fff' }}>{dispNombre}</strong>}
          </div>
          <button className="btn btn-sm portal-pdf-btn" onClick={exportPDF} disabled={exporting}>
            {exporting ? 'Generando…' : 'Descargar PDF'}
          </button>
        </div>
      </div>

      <div className={`portal-content${isBranded ? ' cot-branded' : ''}`} ref={pdfRef}>
        {/* PAGE 1+: COVER with auto-pagination for tareas table */}
        {(() => {
          const ROWS_FIRST_PAGE = 4
          const ROWS_CONT_PAGE = 6

          const tareasChunks: CotizacionTarea[][] = []
          if (isTareas && tareasItems.length > ROWS_FIRST_PAGE) {
            tareasChunks.push(tareasItems.slice(0, ROWS_FIRST_PAGE))
            let remaining = tareasItems.slice(ROWS_FIRST_PAGE)
            while (remaining.length > 0) {
              tareasChunks.push(remaining.slice(0, ROWS_CONT_PAGE))
              remaining = remaining.slice(ROWS_CONT_PAGE)
            }
          }
          const needsPagination = isTareas && tareasChunks.length > 1

          const renderTareaRow = (t: CotizacionTarea, globalIdx: number) => {
            const tc = t.costos
            const totalMTok = tc ? r2(tc.tokens_input_mtok + tc.tokens_thinking_mtok + tc.tokens_output_mtok) : null
            return (
              <tr key={globalIdx}>
                <td style={{ fontWeight: 700, color: 'var(--text-muted)' }}>{globalIdx + 1}</td>
                <td>
                  <a className="cot-task-link" onClick={() => document.getElementById(`tarea-${globalIdx}`)?.scrollIntoView({ behavior: 'smooth' })}>{t.titulo}</a>
                  {t.descripcion_corta && <div style={{ fontSize: '8pt', color: 'var(--text-muted)', fontWeight: 400, marginTop: 2 }}>{t.descripcion_corta}</div>}
                  <a className="cot-task-view-link" onClick={() => document.getElementById(`tarea-${globalIdx}`)?.scrollIntoView({ behavior: 'smooth' })}>Ver detalle →</a>
                </td>
                <td>{t.horas_estimadas ? `${t.horas_estimadas}h` : '—'}</td>
                <td>{totalMTok !== null ? `${totalMTok}M` : '—'}</td>
                <td>{formatCurrency(t.monto, cot.moneda)}</td>
              </tr>
            )
          }

          const tareasTableHead = (
            <thead><tr><th style={{ width: '6%' }}>#</th><th style={{ width: '42%' }}>Tarea</th><th style={{ width: '14%' }}>Horas</th><th style={{ width: '18%' }}>Tokens</th><th style={{ width: '20%' }}>Monto<br/><span style={{ fontSize: '7pt', fontWeight: 400, color: 'var(--text-muted)' }}>s/IGV</span></th></tr></thead>
          )

          const firstPageTareas = needsPagination ? tareasChunks[0] : tareasItems

          return (
            <>
              <div className="cotizacion-preview">
                <div className="cot-header" style={{ background: `linear-gradient(135deg, ${pc} 0%, ${pc}cc 100%)` }}>
                  <div className="cot-header-left">
                    {brand?.logo_url
                      ? <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', display: 'inline-block' }}><img src={brand.logo_url} alt={dispNombre} style={{ height: 26, width: 'auto', display: 'block' }} /></div>
                      : isBranded ? <div className="cot-logo-text">{dispNombre}</div>
                      : empresa.logo_url ? <img src={empresa.logo_url} className="cot-logo" alt={empresa.nombre ?? ''} /> : <div className="cot-logo-text">{dispNombre}</div>}
                  </div>
                  <div className="cot-header-right">
                    {brand?.logo_url_2 ? (
                      <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', display: 'inline-block' }}><img src={brand.logo_url_2} alt={brand.nombre_2 || ''} style={{ height: 26, width: 'auto', display: 'block' }} /></div>
                    ) : !isBranded ? (
                      <>
                        <div className="cot-header-fiscal">{empresa.razon_social || ''}</div>
                        <div>RUC {empresa.ruc || ''}</div>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="cot-title-banner" style={{ borderLeft: `4px solid ${pc}` }}>
                  <div className="cot-title-label">{isEquipo ? 'PROPUESTA DE EQUIPO DEDICADO' : isTareas ? 'PROPUESTA DE SERVICIOS POR TAREA' : 'PROPUESTA DE SERVICIOS'}</div>
                  {(isEquipo || isTareas)
                    ? <div className="cot-title-amount">
                        {isEquipo ? 'INVERSIÓN MENSUAL' : 'PRESUPUESTO'}:{' '}
                        {cot.descuento_monto > 0 && (
                          <span className="cot-title-amount-old">{formatCurrency(r2(cot.total + cot.descuento_monto), cot.moneda)}</span>
                        )}
                        <strong>{formatCurrency(cot.total, cot.moneda)}</strong>
                      </div>
                    : <div className="cot-title-amount" style={{ fontSize: '16pt' }}><strong>{proyectoNombre}</strong></div>
                  }
                </div>

                {(isEquipo || isTareas) && (
                <div className="cot-meta">
                  <div className="cot-meta-block">
                    <h4>Cotizado a</h4>
                    <div className="cot-meta-grid">
                      <span className="cot-meta-key">E:</span><span>{cot.cliente_nombre || '—'}</span>
                      <span className="cot-meta-key">C:</span><span>{cot.cliente_contacto || '—'}</span>
                      <span className="cot-meta-key">T:</span><span>{cot.cliente_telefono || '—'}</span>
                      <span className="cot-meta-key">@:</span><span>{cot.cliente_email || '—'}</span>
                    </div>
                  </div>
                  <div className="cot-meta-block" style={{ textAlign: 'right' }}>
                    <h4>Detalles</h4>
                    <div className="cot-meta-grid" style={{ justifyItems: 'end' }}>
                      <span className="cot-meta-key">N:</span><span>{cot.numero}</span>
                      <span className="cot-meta-key">F:</span><span>{formatDate(cot.fecha_emision)}</span>
                      <span className="cot-meta-key">D:</span><span>Validez: {cot.validez_dias} días calendario</span>
                    </div>
                  </div>
                </div>
                )}

                <div className="cot-body">
                  <p className="cot-intro">
                    {isEquipo
                      ? <>A través del presente documento, ponemos a su disposición la propuesta de <strong>{proyectoNombre}</strong>. A continuación, se detalla la composición del equipo profesional dedicado y la inversión mensual asociada.</>
                      : isTareas
                      ? <>A través del presente documento, ponemos a su disposición la propuesta económica correspondiente. En las siguientes secciones encontrará el desglose detallado de cada requerimiento y la inversión asociada.</>
                      : <>A través del presente documento, ponemos a su disposición la propuesta correspondiente al servicio de <strong>{proyectoNombre}</strong>. En las siguientes secciones encontrará el detalle del servicio, los planes disponibles y la inversión asociada.</>
                    }
                  </p>


                  {isEquipo ? (
                    <>
                      <h3 className="cot-section-title" style={{ borderColor: pc }}>Composición del Equipo</h3>
                      <table className="cot-table">
                        <thead><tr><th style={{ width: '40%' }}>Rol Profesional</th><th style={{ width: '15%' }}>Cantidad</th><th style={{ width: '25%' }}>Dedicación</th><th style={{ width: '20%' }}>Horas / Mes</th></tr></thead>
                        <tbody>
                          {equipoMembers.map((m, i) => (
                            <tr key={i}><td><strong>{m.rol_comercial}</strong> ({m.seniority})</td><td>{m.cantidad}</td><td>{fmtFTE(m)}</td><td>{m.horas_mensuales_linea}h</td></tr>
                          ))}
                          <tr style={{ background: 'var(--bg-secondary, #f5f5f5)', fontWeight: 700 }}>
                            <td colSpan={3}>Total Horas del Equipo</td>
                            <td>{equipoTotales?.total_horas_equipo ?? 0}h</td>
                          </tr>
                        </tbody>
                      </table>
                    </>
                  ) : isTareas ? (
                    <div className="cot-tareas-summary" id="resumen">
                      <h3 className="cot-section-title" style={{ borderColor: pc }}>Desglose de Tareas</h3>
                      <table className="cot-table">
                        {tareasTableHead}
                        <tbody>
                          {firstPageTareas.map((t, i) => renderTareaRow(t, i))}
                        </tbody>
                      </table>
                      {!needsPagination && secciones.length > 0 && <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>}
                    </div>
                  ) : (
                    /* Estándar: cover page with index */
                    secciones.length > 0 && (
                      <div className="cot-index-box" style={{ marginTop: 24 }}>
                        <h3>Contenido</h3>
                        {secciones.map((s, i) => <div key={i} className="cot-index-item" style={{ cursor: 'pointer' }} onClick={() => document.getElementById(`seccion-${i}`)?.scrollIntoView({ behavior: 'smooth' })}><strong>{s.titulo}</strong><span className="cot-index-dots" /><span className="cot-index-page">{i + 2}</span></div>)}
                      </div>
                    )
                  )}

                  {!isTareas && isEquipo && (
                    <>
                      <div className="cot-bottom-row">
                        <DatosBancarios empresa={empresa} moneda={cot.moneda} ocultarCuentas={isBranded} />
                        <div className="cot-totals-box">
                          <div className="cot-totals-row"><span>Subtotal Equipo</span><span>{formatCurrency(cot.subtotal, cot.moneda)}</span></div>
                          <div className="cot-totals-row"><span>IGV ({cot.igv_porcentaje}%)</span><span>{formatCurrency(cot.igv_monto, cot.moneda)}</span></div>
                          {tier.label !== 'Sin descuento' ? (
                            <div className="cot-totals-row cot-discount-row">
                              <span className="cot-discount-label"><span className="cot-discount-badge" style={{ background: tier.color }}>{tier.emoji} {tier.label}</span><span className="cot-discount-pct">-{fmtPct(cot.descuento_porcentaje)}%</span></span>
                              <span>-{formatCurrency(cot.descuento_monto, cot.moneda)}</span>
                            </div>
                          ) : (
                            <div className="cot-totals-row"><span>Descuento 0%</span><span>{formatCurrency(0, cot.moneda)}</span></div>
                          )}
                          <div className="cot-totals-row total" style={{ background: pc }}><span>INVERSIÓN MENSUAL</span><span>{formatCurrency(cot.total, cot.moneda)}</span></div>
                        </div>
                      </div>
                      {secciones.length > 0 && <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>}
                    </>
                  )}
                </div>

                <div className="cot-footer" style={{ background: pc }}>
                  <span>{dispNombre}</span>
                  <span>{isBranded ? '' : `${empresa.ubicacion || ''} | ${empresa.telefono || ''}`}</span>
                  <span>{dispWebsite}</span>
                </div>
              </div>

              {/* CONTINUATION PAGES: remaining task rows + bank info merged when space allows */}
              {needsPagination && tareasChunks.slice(1).map((chunk, chunkIdx) => {
                const globalOffset = ROWS_FIRST_PAGE + chunkIdx * ROWS_CONT_PAGE
                const isLastChunk = chunkIdx === tareasChunks.length - 2
                const mergeBankInfo = isLastChunk && chunk.length <= 4
                return (
                  <div key={`cover-cont-${chunkIdx}`} className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
                    <div className="cot-page-header" style={{ background: pc }} />
                    <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
                    <div className="cot-body" style={{ paddingTop: 40 }}>
                      <h3 className="cot-section-title" style={{ borderColor: pc }}>Desglose de Tareas (cont.)</h3>
                      <table className="cot-table">
                        {tareasTableHead}
                        <tbody>
                          {chunk.map((t, i) => renderTareaRow(t, globalOffset + i))}
                        </tbody>
                      </table>
                      {isLastChunk && secciones.length > 0 && (
                        <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>
                      )}
                      {mergeBankInfo && (
                        <>
                          <h3 className="cot-section-title" style={{ borderColor: pc, marginTop: 24 }}>Datos de Pago</h3>
                          <div className="cot-bottom-row">
                            <DatosBancarios empresa={empresa} moneda={cot.moneda} ocultarCuentas={isBranded} />
                          </div>
                          <div className="cot-validez">Esta cotización tiene una validez de {cot.validez_dias} días calendario a partir de la fecha de emisión.</div>
                        </>
                      )}
                    </div>
                    <div className="cot-footer" style={{ background: pc }}>
                      <span>{dispNombre}</span>
                      <span>Pág. {chunkIdx + 2}</span>
                      <span>{dispWebsite}</span>
                    </div>
                  </div>
                )
              })}
            </>
          )
        })()}

        {/* PAGE 1.5: BANK INFO (tareas only — separate page ONLY if not already merged) */}
        {isTareas && (() => {
          const ROWS_FIRST_PAGE = 4
          const ROWS_CONT_PAGE = 6
          const totalRows = tareasItems.length
          const needsPagination = totalRows > ROWS_FIRST_PAGE
          const lastChunkSize = needsPagination ? ((totalRows - ROWS_FIRST_PAGE) % ROWS_CONT_PAGE || ROWS_CONT_PAGE) : 0
          const wasMerged = needsPagination && lastChunkSize <= 4
          if (wasMerged) return null
          return (
          <div className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body" style={{ paddingTop: 40 }}>
              <h3 className="cot-section-title" style={{ borderColor: pc }}>Datos de Pago</h3>
              <div className="cot-bottom-row">
                <DatosBancarios empresa={empresa} moneda={cot.moneda} ocultarCuentas={isBranded} />
              </div>
              <div className="cot-validez">Esta cotización tiene una validez de {cot.validez_dias} días calendario a partir de la fecha de emisión.</div>
            </div>
            <div className="cot-footer" style={{ background: pc }}>
              <span>{dispNombre}</span>
              <span>Pág. 2</span>
              <span>{dispWebsite}</span>
            </div>
          </div>
          )
        })()}

        {/* INDEX PAGE removed — index is now on cover page */}

        {/* SECTION PAGES — auto-paginated to fit A4 */}
        {secciones.map((sec, idx) => {
          // Calculate start page: page 1 is cover, then each prior section may span multiple pages
          const sectionStartPage = idx + 2
          return (
            <PaginatedSection
              key={idx}
              titulo={sec.titulo}
              contenido={sec.contenido}
              pc={pc}
              empresaNombre={dispNombre}
              empresaWebsite={dispWebsite}
              clienteRuc={cot.cliente_ruc || ''}
              startPage={sectionStartPage}
              sectionId={`seccion-${idx}`}
              bloques={cot}
            />
          )
        })}

        {/* TASK DETAIL PAGES (only for tipo tareas) — auto-paginated by h4 sections */}
        {isTareas && tareasItems.map((tarea, idx) => {
          const baseOffset = 2 + (secciones.length > 0 ? 1 + secciones.length : 0)
          const startPage = baseOffset + idx * 3 + 1
          return (
            <TaskDetailPages
              key={`tarea-${idx}`}
              tarea={tarea}
              idx={idx}
              totalTareas={tareasItems.length}
              pc={pc}
              moneda={cot.moneda}
              empresaNombre={dispNombre}
              empresaWebsite={dispWebsite}
              clienteRuc={cot.cliente_ruc || ''}
              startPage={startPage}
              formatCurrency={formatCurrency}
              processContent={processContent}
            />
          )
        })}
      </div>
    </div>
  )
}
